import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import type { LifetimeParameters } from '@hub/bearing-hub/common/types';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { getLocale, LOCALES_WITH_IGLIDE_BRAND_NAME, materialBrandName } from '@hub/libs/constants';
import type { ParsedUrlQuery } from 'querystring';
import { queryParamsHandler } from '@hub/bearing-hub/BearingHubPage/libs/queryParamsHandler';
import { BearingHubHelper, SHAPE_DESCRIPTORS, SUPPORTED_IGLIDE_IBH_LOCALES } from '@hub/libs/bearing-hub.helper';
import type { GetServerSidePropsContext, PreviewData } from 'next';
import type { LanguageAlternate, ProductDetailsParams } from '@hub/models/data';
import { getArticleName } from '@hub/bearing-hub/common/articleName.factory';
import type {
  IbhCatalogArticleMatrixDetailsDto,
  IbhCatalogArticleMatrixShapesDto,
  IbhPartsResponseDto,
  IbhShape,
  IbhShapeResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { fetchCatalogArticlesMap } from '@hub/bearing-hub/common/fetchCatalogArticlesMap';
import { UnitSystem } from '@igus/kopla-domain';
import { getLengthCode, getUnitSystemPrefix } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';
import { defaultLocale } from '@icommerce/i18n';

export const productDetailsSlug = '/product-details/';

export const getMaterialBrandName = (locale: string) => {
  return LOCALES_WITH_IGLIDE_BRAND_NAME.includes(locale) ? materialBrandName.iglide : materialBrandName.iglidur;
};

export const getCategoryName = (materialId: string, unitSystem: UnitSystem, locale: string) => {
  return encodeURIComponent(
    `${getMaterialBrandName(locale)}-${materialId.toLowerCase()}-${getUnitSystemPrefix(unitSystem)}`
  );
};
export enum IbhProductionMethodCode {
  IM = 'IM',
  SF = 'SF',
  MR = 'MR',
  FL = 'FL',
  ThreeD = '3D',
}

const mapProductionMethod = (code?: string): IbhProductionMethods => {
  switch ((code || IbhProductionMethodCode.IM).toUpperCase()) {
    case IbhProductionMethodCode.IM:
      return IbhProductionMethods.MOLDINJECTION;
    case IbhProductionMethodCode.SF:
      return IbhProductionMethods.SEMIFINISHED;
    case IbhProductionMethodCode.MR:
      return IbhProductionMethods.MECHANICALREPROCESSING;
    case IbhProductionMethodCode.FL:
      return IbhProductionMethods.FASTLINE;
    case IbhProductionMethodCode.ThreeD:
      return IbhProductionMethods.THREEDIMENSIONALPRINT;
    default:
      return IbhProductionMethods.MOLDINJECTION;
  }
};

export const getArticleDataFromContext = (
  context: GetServerSidePropsContext<ProductDetailsParams, PreviewData>,
  bearingShapes: IbhShapeResponseDto[]
): {
  materialName: string;
  unitSystem: UnitSystem;
  isCustom: boolean;
  selectedShapeDescriptor: ShapeDescriptor;
  productionMethod: IbhProductionMethods;
} => {
  const { shape } = context.params;
  const slug = context.params.categoryName;
  const artnr = context.query?.artnr as string;
  const locale = getLocale(context);
  const selectedShapeDescriptor = BearingHubHelper.getShapeByShapePath(shape, locale, bearingShapes);
  const customArticlePrefix = 'TEMP';

  // [ 'i180', 'bl', 'pf', 'm' ] or [ 'g', 'm' ] example of slugPieces
  const slugWithoutPrefix = slug.replace(/^(iglide|iglidur)-/, '');
  const slugPieces = slugWithoutPrefix.split('-');
  const isCustom = artnr?.includes(customArticlePrefix) || false;
  const productionMethodFromSlug = isCustom ? artnr.match(/^TEMP-([^-]+)/)[1] : 'IM';
  const productionMethod = mapProductionMethod(productionMethodFromSlug);
  const unitSystemPiece = slugPieces.slice(-1)[0];
  const materialName = slugPieces.slice(0, -1).join('-').toUpperCase();
  const unitSystem = unitSystemPiece === 'm' ? UnitSystem.metric : UnitSystem.imperial;

  return {
    materialName,
    unitSystem,
    isCustom,
    selectedShapeDescriptor,
    productionMethod,
  };
};

export const getRedirectLinkToProductDetails = (
  part: IbhPartsResponseDto,
  selectedShape: ShapeDescriptor,
  unitSystem: UnitSystem,
  hashParam: ParsedUrlQuery,
  isCustom: boolean,
  locale: string
) => {
  const pathNameToResultPage = `${SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'}/${
    selectedShape.shapePath
  }`;
  const pathNameToProductDetailsPage = 'product-details';
  const categoryName = getCategoryName(part.material.id, unitSystem, locale);
  const properArticleNumber = getArticleName(locale, part);
  const query = isCustom
    ? `${properArticleNumber}_${getLengthCode(unitSystem).toUpperCase()}${BearingHubHelper.getDimensionsParam(
        part.dimensions,
        selectedShape
      )}`
    : properArticleNumber;
  const hash = queryParamsHandler.createHashParam(hashParam).toString();
  const articleNumber = part.smallestForMaterial ? '' : `?artnr=${query}`;

  return `/${pathNameToResultPage}/${pathNameToProductDetailsPage}/${categoryName}${articleNumber}${
    hash ? `#${hash}` : ''
  }`;
};

export const getCanonicalLink = (languageAlternates: LanguageAlternate[], locale: string) =>
  languageAlternates.find(item => item.hrefLang === locale)?.href.split('?')[0];

export const getRedirectLinkToResults = (
  selectedShape: ShapeDescriptor,
  quantity: number,
  lifetimeParameters: LifetimeParameters,
  locale: string,
  unitSystem: UnitSystem,
  dimensions?: IbhDimensions | null,
  materialId?: string,
  formUnitSystem?: UnitSystem
) => {
  const pathNameToResultPage = `${SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'}/${
    selectedShape.shapePath
  }`;
  const lifeTime = queryParamsHandler.getServiceLifeParams(lifetimeParameters, formUnitSystem ?? unitSystem);
  const dimensionsParam = BearingHubHelper.getDimensionsParam(dimensions ?? null, selectedShape, true);
  const unitSystemHash = unitSystem === UnitSystem.imperial ? 'unitSystem=imperial' : '';
  const quantityHash = quantity > 1 ? `quantity=${quantity}` : '';
  const dimensionsHash = dimensionsParam ? `dimensions=${dimensionsParam}` : '';
  const lifeTimeHash = lifeTime ? `lifetimeParameters=${lifeTime}` : '';
  const formUnitSystemHash = formUnitSystem
    ? `formUnitSystem=${queryParamsHandler.getFormUnitSystemParam(formUnitSystem)}`
    : '';
  const materialIdHash = materialId ? `material=${materialId}` : '';
  const hashParts = [
    unitSystemHash,
    quantityHash,
    dimensionsHash,
    lifeTimeHash,
    formUnitSystemHash,
    materialIdHash,
  ].filter(Boolean);
  const hash = hashParts.length ? `#${hashParts.join('&')}` : '';
  return `/${pathNameToResultPage}${hash}`;
};

export const getLinkForResultPage = (shape: IbhShape, locale: string) => {
  const shapeDescriptors = SHAPE_DESCRIPTORS[locale] || SHAPE_DESCRIPTORS[defaultLocale];
  const shapePath = shapeDescriptors[shape].path;
  const defaultPathToResultPage = `${
    SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale) ? 'iglide-ibh' : 'iglidur-ibh'
  }/${shapePath}`;

  return `/${defaultPathToResultPage}`;
};

export const getLinkForDetailPage = (article: IbhCatalogArticleMatrixDetailsDto, shape: IbhShape, locale: string) => {
  const { materialCode, unitSystem } = article;
  const pathToResultPage = getLinkForResultPage(shape, locale);
  const categoryName = getCategoryName(materialCode, unitSystem as unknown as UnitSystem, locale);

  return `${pathToResultPage}${productDetailsSlug}${categoryName}`;
};

export const getLinksForShape = (articles: IbhCatalogArticleMatrixDetailsDto[], shape: IbhShape, locale: string) => {
  const linkToResultPage = getLinkForResultPage(shape, locale);
  const linksToProductDetailsPage = articles?.map(article => getLinkForDetailPage(article, shape, locale));

  if (!linksToProductDetailsPage) {
    return [linkToResultPage];
  }
  return [linkToResultPage, ...linksToProductDetailsPage];
};

/**
 * Return all IBH links
 *
 * @param shapes - list of supported shapes (e.g. [IbhShape.S, IbhShape.F])
 * @param locale - given locale (e.g. 'en-US')
 */
export const getIbhSiteMap = async (locale: string, shapes?: IbhShape[]) => {
  const normalizedLocale = locale.includes('-') ? locale.split('-')[1] : locale.toUpperCase();
  const shapeDescriptors = SHAPE_DESCRIPTORS[locale] || SHAPE_DESCRIPTORS[defaultLocale];
  const defaultShapesList = Object.keys(shapeDescriptors) as IbhShape[];
  const shapeToUse = shapes ?? defaultShapesList;
  const articlesMatrix = await fetchCatalogArticlesMap(shapes, [normalizedLocale]);
  const articlesMap = (articlesMatrix[normalizedLocale] as IbhCatalogArticleMatrixShapesDto) || articlesMatrix.default;

  return shapeToUse.flatMap(shape => getLinksForShape(articlesMap[shape], shape, locale));
};
